import { useBreakpoint } from "@/hooks/useBreakpoint";
import type { LocationState } from "@/types/pane-routing";
import React, {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// The consolidated Workspaces rail nests canvases/chats/files, so the separate
// files/chats rails were retired. "canvas" and "chat" are the contextual rails
// for the open canvas / open chat — their tabs only exist while on the matching
// route. Navigating from one contextual route to the other hands the selection
// off (canvas ⇄ chat); leaving both snaps back to "workspaces".
export type WorkspaceRail =
  | "workspaces"
  | "chats"
  | "skills"
  | "automations"
  | "connectors";

// DS left-pane spec: the docked pane rests at 280 (`--bin-w`), capped at
// 440 (RAIL_W_MAX). The floor keeps main's battle-tested 256 rather than
// the spec's 280 — the comp judged legibility on fixture content, while
// production rows held up narrower for months (Aug 2026 ruling).
export const WORKSPACE_RAIL_DEFAULT_WIDTH = 280;
export const WORKSPACE_RAIL_MIN_WIDTH = 256;
export const WORKSPACE_RAIL_MAX_WIDTH = 440;
/** Nav zone width when rail is collapsed: size-8 (32px) + px-2 (8px) each side. */
export const WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH = 48;
/** Mobile header nav when drawer is closed — three size-8 icons plus the
 *  contextual canvas/chat tab, gap-0.5, px-2. */
export const WORKSPACE_RAIL_MOBILE_NAV_WIDTH = 150;

const RAIL_OPEN_STORAGE_KEY = "workspace-rail-open";

function readRailOpenPreference(): boolean {
  try {
    const stored = localStorage.getItem(RAIL_OPEN_STORAGE_KEY);
    if (stored === "false") return false;
    return true;
  } catch {
    return true;
  }
}

function writeRailOpenPreference(open: boolean) {
  try {
    localStorage.setItem(RAIL_OPEN_STORAGE_KEY, String(open));
  } catch {
    /* ignore */
  }
}

interface SidebarContextType {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;

  leftPanelWidth: number;
  setLeftPanelWidth: (width: number) => void;
  rightPanelWidth: number;
  setRightPanelWidth: (width: number) => void;

  railOpen: boolean;
  setRailOpen: (open: boolean) => void;
  toggleRail: () => void;
  activeRail: WorkspaceRail;
  setActiveRail: (rail: WorkspaceRail) => void;
  selectRail: (rail: WorkspaceRail) => void;
  railWidth: number;
  setRailWidth: (width: number) => void;
  /** Synchronously updates nav zone DOM width during drag (bypasses React render). */
  applyRailWidthToNavZone: (width: number) => void;
  /** Latest rail width — updated synchronously; use during drag when React state lags. */
  railWidthRef: RefObject<number>;
  /** Subscribe to per-frame rail widths during drags (imperative channel). */
  subscribeRailLiveWidth: (listener: (width: number) => void) => () => void;
  navZoneRef: RefObject<HTMLDivElement | null>;
  railResizing: boolean;
  setRailResizing: (resizing: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebarContext must be used within a SidebarProvider");
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const lg = useBreakpoint("lg");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [leftPanelWidth, setLeftPanelWidth] = useState(0);
  const [rightPanelWidth, setRightPanelWidth] = useState(0);

  const [railOpen, setRailOpenState] = useState(readRailOpenPreference);
  const [activeRail, setActiveRail] = useState<WorkspaceRail>("workspaces");
  const [railWidth, setRailWidthState] = useState(WORKSPACE_RAIL_DEFAULT_WIDTH);
  const [railResizing, setRailResizing] = useState(false);
  const navZoneRef = useRef<HTMLDivElement | null>(null);
  const railWidthRef = useRef(WORKSPACE_RAIL_DEFAULT_WIDTH);

  // Per-frame rail-width channel. The rail reports live drag widths through
  // `applyRailWidthToNavZone` (imperative — React state only learns the width
  // at drag end, deliberately, so rail drags don't re-render the world). Some
  // layout math must track those frames anyway — the full-width-expanded
  // right pane's width is `window − rail − chatDock` — so live frames fan out
  // to explicit subscribers, who apply them imperatively too. One-way flow:
  // nothing a subscriber does feeds back into the rail's width.
  const railLiveListenersRef = useRef(new Set<(width: number) => void>());
  const subscribeRailLiveWidth = useCallback(
    (listener: (width: number) => void) => {
      railLiveListenersRef.current.add(listener);
      return () => {
        railLiveListenersRef.current.delete(listener);
      };
    },
    [],
  );

  const applyRailWidthToNavZone = useCallback((width: number) => {
    railWidthRef.current = width;
    navZoneRef.current?.style.setProperty("width", `${width}px`);
    for (const listener of railLiveListenersRef.current) listener(width);
  }, []);

  const setRailWidth = useCallback((width: number) => {
    railWidthRef.current = width;
    setRailWidthState(width);
    for (const listener of railLiveListenersRef.current) listener(width);
  }, []);

  const setRailOpen = useCallback(
    (open: boolean) => {
      if (lg) {
        setRailOpenState(open);
        writeRailOpenPreference(open);
      } else {
        setIsDrawerOpen(open);
      }
    },
    [lg],
  );

  const toggleRail = useCallback(() => {
    if (lg) {
      setRailOpenState((prev) => {
        const next = !prev;
        writeRailOpenPreference(next);
        return next;
      });
    } else {
      setIsDrawerOpen((prev) => !prev);
    }
  }, [lg]);

  const selectRail = useCallback(
    (rail: WorkspaceRail) => {
      setActiveRail(rail);
      if (lg) {
        setRailOpenState(true);
        writeRailOpenPreference(true);
      } else {
        setIsDrawerOpen(true);
      }
    },
    [lg],
  );

  useEffect(() => {
    if (lg) {
      setIsDrawerOpen(false);
    } else {
      const state = location.state as LocationState | null;
      if (!state?.rightPane && !state?.rightPaneHistory) return;
      const nextState: LocationState = { ...state };
      delete nextState.rightPane;
      delete nextState.rightPaneHistory;
      navigate(location.pathname + location.search + location.hash, {
        replace: true,
        state: nextState,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on breakpoint change
  }, [lg]);

  useEffect(() => {
    if (!lg) {
      setIsDrawerOpen(false);
    }
  }, [location.pathname, lg]);

  return (
    <SidebarContext.Provider
      value={{
        isDrawerOpen,
        setIsDrawerOpen,

        leftPanelWidth,
        setLeftPanelWidth,
        rightPanelWidth,
        setRightPanelWidth,

        railOpen,
        setRailOpen,
        toggleRail,
        activeRail,
        setActiveRail,
        selectRail,
        railWidth,
        setRailWidth,
        applyRailWidthToNavZone,
        railWidthRef,
        subscribeRailLiveWidth,
        navZoneRef,
        railResizing,
        setRailResizing,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const withSidebarContext = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
): React.FC<P> => {
  return (props: P) => (
    <SidebarProvider>
      <WrappedComponent {...props} />
    </SidebarProvider>
  );
};
